import { useQuery } from '@tanstack/react-query'
import { queryClient } from './queryClient.js'
import { auth as authApi } from './api.js'
import { clearCachedConfig } from './config.js'

/**
 * Session state. The JWT lives in an httpOnly same-origin cookie, so there is
 * nothing here that reads, stores or attaches a token — the browser does it.
 * "Am I signed in?" is a question only the server can answer.
 */

export const ME_KEY = ['me']

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    // 401 is a legitimate answer here, not session loss, so it must not trip the
    // global redirect. api.auth.me passes allowUnauthenticated for that reason.
    queryFn: async () => {
      try {
        return await authApi.me()
      } catch (err) {
        if (err.status === 401) return null
        throw err
      }
    },
    retry: false,
    staleTime: 60_000,
  })
}

export async function login(credentials) {
  const me = await authApi.login(credentials)
  queryClient.setQueryData(ME_KEY, me ?? null)
  await queryClient.invalidateQueries({ queryKey: ME_KEY })
  return me
}

export async function register(payload) {
  const me = await authApi.register(payload)
  queryClient.setQueryData(ME_KEY, me ?? null)
  await queryClient.invalidateQueries({ queryKey: ME_KEY })
  return me
}

export async function logout() {
  try {
    await authApi.logout()
  } finally {
    // Whatever the server said, this device is done. Clearing the whole cache
    // matters: another participant must never see a cached prompt or answer.
    queryClient.clear()
    // Memory is not the only copy. The config bundle is also in localStorage,
    // where it used to survive sign-out and stay readable from the storage
    // inspector by whoever picks the phone up next.
    clearCachedConfig()
  }
}
